import {
  ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import { DrawerProps } from '../../Components/Drawer';
import Loader from '../../Components/Loader';
import Modal from '../../Components/Modal';
import SequentialModals from '../../Components/SequentialModals';
import { assetsPreload } from '../../PIXIHelpers';
import { toggleMute } from '../../helpers';
import { useAppDispatch, useAppSelector } from '../../store';
import { authenticateUser, refreshAuthToken } from '../../store/reducers/auth/asyncActions';
import { getActiveSlotEffects, getAviatorHistory } from '../../store/reducers/aviator/asyncActions';
import { getActiveChests, getChests } from '../../store/reducers/chests/asyncActions';
import { getTopLeaguesReward, getTopPlayers } from '../../store/reducers/leaderboard/asyncActions';
import {
  getCharacterUpdate,
  getCharacterUpdateReward,
  getMe,
  updateTGProfile,
} from '../../store/reducers/profile/asyncActions';
import { getQuests } from '../../store/reducers/quests/asyncActions';
import { getSettings, getVersion } from '../../store/reducers/settings/asyncActions';
import { getAutopilot, getPurchases, getShopItems } from '../../store/reducers/store/asyncActions';
import DailyRewardsModal from '../../templates/DailyRewardsModal';
import SpinAndClaimRewards from '../../templates/SpinAndClaimRewards';
import OverlayGame from './OverlayGame';
import { preloadOverlay } from './overlayApp';
import LeaguePromotionModal from '../../templates/LeaguePromotionModal';
import { getPayments } from '../../store/reducers/payments/asyncActions';
import eventEmitter, { GameEvents } from '../../pages/Home/eventEmiter';
import GlobalGame from '../../PhaserGameClass/GlobalGame';
import { SlotMachinePixiApp } from '../../apps/slotMachine';
import useAppModalsStatus from '../../hooks/useAppModalsStatus';
import TutorialModal from '../../templates/TutorialModal';
import { preloadTutorialImages } from '../../helpers/preloader';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { version: appVersion } = require('../../../package.json');

interface AppWrapperProps {
    children: ReactNode
}

const tg = window?.Telegram.WebApp;

GlobalGame.scene.start('PreloadScene');

const slotGame = SlotMachinePixiApp.getInstance();

function AppWrapper({ children }: AppWrapperProps) {
  const dispatch = useAppDispatch();

  const {
    loading: profileLoading,
  } = useAppSelector(({ profile }) => profile);
  const {
    loading: questsLoading,
  } = useAppSelector(({ quests }) => quests);
  const {
    loading: chestsLoading, chestsHistory,
  } = useAppSelector(({ chests }) => chests);
  const {
    version: beVersion, loading: settingsLoading, settings: AppSetting,
  } = useAppSelector(({ settings }) => settings);
  const { loading: paymentsLoading } = useAppSelector(({ payments }) => payments);
  const { error: authError, loading: authLoading } = useAppSelector(({ auth }) => auth);

  const [overlayLoading, setOverlayLoading] = useState<boolean>(true);
  const [slotLoading, setSlotLoading] = useState<boolean>(true);
  const [assetsLoading, setAssetsLoading] = useState<boolean>(true);
  const [phaserLoading, setPhaserLoading] = useState<boolean>(true);

  useEffect(() => {
    toggleMute(!!AppSetting?.enable_sound);
  }, [AppSetting?.enable_sound]);

  const handleAuth = async () => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      await dispatch(refreshAuthToken());
    } else {
      await dispatch(authenticateUser(tg.initData));
    }
  };
  useEffect(() => {
    const handleSceneLoaded = () => {
      setPhaserLoading(false);
    };
    eventEmitter.on(GameEvents.PRELOAD_LOADED, handleSceneLoaded);
    return () => {
      eventEmitter.off(GameEvents.PRELOAD_LOADED, handleSceneLoaded);
    };
  }, []);
  const initialLoadingData = async () => { // initial data loading to get all pages data on loading screen
    await dispatch(getMe());
    await Promise.all([
      dispatch(getVersion()),
      dispatch(getSettings()),
      dispatch(getQuests()), // need to show tutorial modal
      dispatch(getTopLeaguesReward()),
      dispatch(getPurchases()),
      dispatch(getAutopilot()),
      dispatch(getAviatorHistory()),
      dispatch(getActiveSlotEffects()),
      dispatch(getActiveChests()),
      dispatch(getChests()),
      dispatch(getPayments()),
      dispatch(getCharacterUpdate()),
    ]);
    await dispatch(getCharacterUpdateReward());
    await dispatch(getQuests());

    preloadOverlay().finally(() => {
      setOverlayLoading(false);
    });
    slotGame.preloadSlot().then(() => {
      setSlotLoading(false);
    });
    assetsPreload().then(() => {
      setAssetsLoading(false);
    });
    dispatch(getShopItems());
    dispatch(getTopPlayers());
    // dispatch(getReferrals()),
  };

  useEffect(() => {
    (async () => {
      await handleAuth();
      await initialLoadingData();
    })();
  }, []);

  useEffect(() => {
    dispatch(updateTGProfile(tg.initDataUnsafe));
    tg?.disableVerticalSwipes();
    window.console.log(tg, 'tg');
  }, [tg.initDataUnsafe]);

  useEffect(() => {
    if (beVersion) {
      window.console.log({
        BE_Version: beVersion,
        FE_Version: appVersion,
      }, 'app version');
    }
  }, [beVersion]);

  const handleReload = () => {
    tg?.close();
  };

  const loadingProgress = useMemo(() => {
    const loadingList = [profileLoading, settingsLoading, questsLoading, authLoading, overlayLoading, slotLoading, assetsLoading, chestsLoading, paymentsLoading, phaserLoading];

    const progress = loadingList.reduce((p, i) => {
      if (!i) {
        return p + 1;
      }

      return p;
    // eslint-disable-next-line no-mixed-operators
    }, 0) / loadingList.length * 100;

    return progress;
  }, [profileLoading, settingsLoading, questsLoading, authLoading, overlayLoading, slotLoading, assetsLoading, chestsLoading, paymentsLoading, phaserLoading]);

  const onCollectRewardsModalClose = async () => {
    await dispatch(getActiveChests());
  };

  const {
    dailyReward, chestHistory, leagues, tutorial,
  } = useAppModalsStatus();

  const renderOpenChestModal = useCallback(
    (props: DrawerProps) => {
      const chestHistoryFiltered = [...chestsHistory || []]
        ?.filter((chest) => chest.obtained_by !== 'tutorial')
        ?.find((cH) => cH.status !== 'claimed');

      if (chestHistoryFiltered && !tutorial && !dailyReward && (props?.id === props?.activeModal?.id || !props.activeModal)) {
        return (
          <SpinAndClaimRewards
            {...props}
            chestHistory={chestHistoryFiltered}
            onCollectClose={async () => {
              await onCollectRewardsModalClose();
              props?.onClose?.();
            }}
          />
        );
      }

      return null;
    },
    [chestsHistory, tutorial, dailyReward],
  );

  const renderTutorialModal = useCallback(
    (props: DrawerProps) => {
      if (tutorial) {
        preloadTutorialImages();
        return (
          <TutorialModal
            {...props}
          />
        );
      }
      return null;
    },
    [tutorial],
  );

  const modals = useMemo(() => [
    { id: 'tutorial-modal', component: renderTutorialModal, enabled: tutorial },
    { id: 'daily-rewards-modal', component: DailyRewardsModalComponent, enabled: dailyReward },
    { id: 'open-chest-modal', component: renderOpenChestModal, enabled: chestHistory },
    { id: 'league-promotion-modal', component: LeaguePromotionModal, enabled: leagues },
  ], [dailyReward, chestHistory, renderOpenChestModal, leagues, tutorial]);

  if (authError) {
    return <Modal message={authError} onReload={handleReload} />;
  }

  if (loadingProgress !== 100) {
    return <Loader progress={loadingProgress} />;
  }

  return (
    <>
      <div id="preload-canvas" />
      <SequentialModals modals={modals} />
      <OverlayGame />
      {children}
    </>
  );
}

export default AppWrapper;
function DailyRewardsModalComponent(props: DrawerProps) {
  return <DailyRewardsModal {...props} />;
}
